import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  Search,
} from "lucide-react";
import { AppDialog, SearchableSelect } from "@/components/common";
import { OrgTreeNodeBreadcrumb } from "@/components/common/OrgTreeNodeBreadcrumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useAuthStore } from "@/features/auth";
import { useTran } from "@/hooks";
import { showToast } from "@/lib/utils/toast";
import { masterDataService } from "../../services";
import { Label } from "recharts";
import { formatReportingYearPeriod } from "../../utils/period-constants";

const getDisclosureKey = (item = {}) =>
  item.disclosureId ? String(item.disclosureId) : "";

const getAssignmentOrganizationValues = (item = {}, companyId) => {
  if (item?.isAllOrganization) return [];

  const values = Array.isArray(item.organizationIds)
    ? item.organizationIds.map(String)
    : [];

  if (item?.isCompanySelected && companyId) {
    return Array.from(new Set([String(companyId), ...values]));
  }

  return values;
};

const normalizeStringSet = (values = []) =>
  new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => String(value || "").trim())
      .filter(Boolean),
  );

const getReportingYearOrganizationValues = (reportingYear = {}, companyId) => {
  if (reportingYear?.isAllOrganization) return [];

  const values = Array.isArray(reportingYear?.organizations)
    ? reportingYear.organizations
        .map((org) => org?._id || org?.id || org?.value || org)
        .filter(Boolean)
        .map(String)
    : [];

  if (reportingYear?.isCompanySelected && companyId) {
    return Array.from(new Set([String(companyId), ...values]));
  }

  return values;
};

const getFrameworkMismatchReason = (item = {}, targetReportingYear = {}) => {
  const targetFrameworks = normalizeStringSet(
    targetReportingYear?.frameworkCodes || [],
  );
  const itemFramework = String(item.framework || "").trim();

  if (!itemFramework || targetFrameworks.size === 0) return "";

  return targetFrameworks.has(itemFramework)
    ? ""
    : "Framework is not included in target reporting year";
};

const getOrganizationMismatchReason = (
  item = {},
  targetReportingYear = {},
  companyId,
) => {
  if (targetReportingYear?.isAllOrganization) return "";

  if (item?.isAllOrganization) {
    return "All organizations are not included in target reporting year";
  }

  const sourceOrganizations = getAssignmentOrganizationValues(item, companyId);
  if (sourceOrganizations.length === 0) return "";

  const targetOrganizations = new Set(
    getReportingYearOrganizationValues(targetReportingYear, companyId),
  );
  const hasMissingOrganization = sourceOrganizations.some(
    (id) => !targetOrganizations.has(id),
  );

  return hasMissingOrganization
    ? "Organization is not included in target reporting year"
    : "";
};

const getCloneEligibility = (
  item = {},
  targetReportingYear = {},
  companyId,
) => {
  const reasons = [
    getFrameworkMismatchReason(item, targetReportingYear),
    getOrganizationMismatchReason(item, targetReportingYear, companyId),
  ].filter(Boolean);

  return {
    isMismatch: reasons.length > 0,
    reason: reasons.join(", "),
  };
};

const getCloneScopeComparison = (source = {}, target = {}, companyId) => {
  if (target?.isAllOrganization) {
    return {
      isSkip: true,
      reason: "Target already covers all organizations",
      newOrganizationIds: [],
      changesToAllOrganizations: false,
    };
  }

  if (source?.isAllOrganization) {
    return {
      isSkip: false,
      reason: "",
      newOrganizationIds: [],
      changesToAllOrganizations: true,
    };
  }

  const sourceIds = getAssignmentOrganizationValues(source, companyId);
  const targetIds = getAssignmentOrganizationValues(target, companyId);
  const targetIdSet = new Set(targetIds);
  const newOrganizationIds = sourceIds.filter((id) => !targetIdSet.has(id));

  if (newOrganizationIds.length === 0) {
    return {
      isSkip: true,
      reason: "Target already has all source organizations",
      newOrganizationIds,
      changesToAllOrganizations: false,
    };
  }

  return {
    isSkip: false,
    reason: "",
    newOrganizationIds,
    changesToAllOrganizations: false,
  };
};

const MetricsCloneDialog = ({
  open,
  onOpenChange,
  items,
  loading,
  organizationTree,
  onCloned,
  reportingYears,
}) => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const [submitting, setSubmitting] = useState(false);
  const [confirmClone, setConfirmClone] = useState(null);
  const [reviewImpactClone, setReviewImpactClone] = useState(null);
  const [reviewClone, setReviewClone] = useState(null);
  const [impactAccepted, setImpactAccepted] = useState(false);
  const [selectedDuplicateIds, setSelectedDuplicateIds] = useState([]);
  const [cloneFilters, setCloneFilters] = useState({
    search: "",
    framework: "All",
    category: "All",
  });
  const [form, setForm] = useState({
    sourceYear: "",
    targetYear: "",
  });
  const companyId =
    user?.companyId || user?.company?._id || user?.company?.id || user?.orgId;

  // const sourceYearOptions = useMemo(() => {
  //   const years = Array.from(
  //     new Set(
  //       items
  //         .map((item) => Number.parseInt(item.reportingYear, 10))
  //         .filter((year) => Number.isFinite(year)),
  //     ),
  //   ).sort((a, b) => b - a);

  //   return years.map((year) => ({
  //     value: String(year),
  //     label: String(year),
  //   }));
  // }, [items]);

  // const targetYearOptions = useMemo(() => {
  //   const parsedStartYear = Number.parseInt(startYear, 10);
  //   const parsedEndYear = Number.parseInt(endYear, 10);
  //   const hasRange =
  //     Number.isFinite(parsedStartYear) && Number.isFinite(parsedEndYear);

  //   if (!hasRange) {
  //     const currentYear = new Date().getFullYear();
  //     const yearSet = new Set(
  //       Array.from({ length: 7 }, (_, index) =>
  //         String(currentYear - 2 + index),
  //       ),
  //     );
  //     sourceYearOptions.forEach((option) => yearSet.add(option.value));
  //     if (form.sourceYear) yearSet.add(form.sourceYear);
  //     if (form.targetYear) yearSet.add(form.targetYear);

  //     return Array.from(yearSet)
  //       .map((year) => Number.parseInt(year, 10))
  //       .filter((year) => Number.isFinite(year))
  //       .sort((a, b) => b - a)
  //       .map((year) => ({
  //         value: String(year),
  //         label: String(year),
  //       }));
  //   }

  //   const minYear = Math.min(parsedStartYear, parsedEndYear);
  //   const maxYear = Math.max(parsedStartYear, parsedEndYear);
  //   const options = [];

  //   for (let year = maxYear; year >= minYear; year -= 1) {
  //     options.push({
  //       value: String(year),
  //       label: String(year),
  //     });
  //   }

  //   return options;
  // }, [endYear, form.sourceYear, form.targetYear, sourceYearOptions, startYear]);

  const sourceYearOptions = useMemo(() => {
    const itemYears = new Set(
      (items || [])
        .map((item) => Number.parseInt(item.reportingYear, 10))
        .filter(Number.isFinite),
    );

    return (reportingYears || [])
      .filter((reportingYear) => itemYears.has(Number(reportingYear.startYear)))
      .slice()
      .sort((a, b) => Number(b.startYear) - Number(a.startYear))
      .map((reportingYear) => ({
        value: String(reportingYear.startYear),
        label: formatReportingYearPeriod(reportingYear),
      }));
  }, [items, reportingYears]);

  const targetYearOptions = useMemo(() => {
    return (reportingYears || [])
      .slice()
      .sort((a, b) => Number(b.startYear) - Number(a.startYear))
      .map((reportingYear) => ({
        value: String(reportingYear.startYear),
        label: formatReportingYearPeriod(reportingYear),
      }));
  }, [reportingYears]);

  useEffect(() => {
    if (!open) {
      setForm({ sourceYear: "", targetYear: "" });
      setConfirmClone(null);
      setReviewImpactClone(null);
      setReviewClone(null);
      setImpactAccepted(false);
      setSelectedDuplicateIds([]);
      setCloneFilters({ search: "", framework: "All", category: "All" });
    }
  }, [open]);

  useEffect(() => {
    const allowedTargetYears = new Set(
      targetYearOptions
        .filter((option) => option.value !== form.sourceYear)
        .map((option) => option.value),
    );

    setForm((prev) => ({
      ...prev,
      targetYear: allowedTargetYears.has(prev.targetYear)
        ? prev.targetYear
        : "",
    }));
  }, [targetYearOptions, form.sourceYear]);

  const renderAssignmentScope = (item, highlightValues = []) => {
    if (item?.isAllOrganization) {
      return (
        <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs font-medium text-foreground">
          {t("All organizations")}
        </div>
      );
    }

    const organizationValues = getAssignmentOrganizationValues(item, companyId);
    if (organizationValues.length === 0) {
      return (
        <div className="rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
          {t("No organization selected")}
        </div>
      );
    }

    return (
      <OrgTreeNodeBreadcrumb
        includeParentCompany={true}
        tree={organizationTree || []}
        value={organizationValues}
        onChange={() => {}}
        placeholder={t("No organization selected")}
        readOnly
        highlightValues={highlightValues}
        className="border-0 bg-transparent px-0 py-0 shadow-none disabled:cursor-default disabled:opacity-100"
      />
    );
  };

  const renderNewOrganizationScope = (row, isNewOrgs = false) => {
    const newOrganizationIds = row.newOrganizationIds || [];

    if (row.changesToAllOrganizations) {
      return (
        <div className="rounded-md border border-green-500 bg-green-100 px-3 py-2 text-xs font-medium text-green-800">
          {t("All organizations")}
        </div>
      );
    }

    if (newOrganizationIds.length === 0) {
      return (
        <div className="rounded-md border border-dashed border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          {t("No new organization selected")}
        </div>
      );
    }

    return (
      <OrgTreeNodeBreadcrumb
        includeParentCompany={true}
        tree={organizationTree || []}
        value={newOrganizationIds}
        onChange={() => {}}
        placeholder={t("No new organization selected")}
        readOnly
        className="border-0 bg-transparent px-0 py-0 shadow-none disabled:cursor-default disabled:opacity-100"
        isNewOrgs={isNewOrgs}
      />
    );
  };

  const renderScopeChangeSummary = (row) => {
    if (row.isSkip) {
      return (
        <Badge variant="outline" className="rounded bg-muted/40">
          {t("No new orgs")}
        </Badge>
      );
    }

    if (row.changesToAllOrganizations) {
      return (
        <Badge className=" px-6 bg-yellow-100 text-yellow-700 hover:bg-yellow-200 ">
          <ArrowRight className="mr-1 h-4 w-4" /> {t("Changes to all orgs")}
        </Badge>
      );
    }

    const newCount = row.newOrganizationIds?.length || 0;

    return (
      <Badge className=" px-6 bg-green-100 text-green-700 hover:bg-green-200">
        + {t("{{count}} new org(s)", { count: newCount })}
      </Badge>
    );
  };

  const renderDuplicateRow = (row, { selectable = false } = {}) => {
    const checked = selectedDuplicateIds.includes(row.disclosureId);

    return (
      <div
        key={row.disclosureId}
        className="rounded-lg border border-border bg-card p-3"
      >
        <div className="flex items-start gap-3">
          {selectable ? (
            <Checkbox
              checked={checked}
              onCheckedChange={(value) => {
                setSelectedDuplicateIds((prev) =>
                  value
                    ? Array.from(new Set([...prev, row.disclosureId]))
                    : prev.filter((id) => id !== row.disclosureId),
                );
              }}
              className="mt-1 h-6 w-6 "
            />
          ) : null}
          <div className="min-w-0 flex-1 space-y-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="rounded">
                  {row.code}
                </Badge>
                <Badge variant="secondary" className="rounded">
                  {row.framework}
                </Badge>
                <Badge variant="outline" className="rounded">
                  {row.category}
                </Badge>
                {renderScopeChangeSummary(row)}
              </div>
              <p className="mt-1 truncate text-sm font-semibold text-foreground">
                {row.title}
              </p>
              {row.isSkip ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t(row.reason)}
                </p>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="flex min-h-32 flex-col rounded-md border border-green-500 bg-green-100 p-3">
                <div className="flex justify-between items-center">
                  <p className="text-xs font-semibold uppercase text-emerald-800">
                    {t("New cloned orgs")}
                  </p>
                  <p className="mt-1 text-sm font-medium text-foreground">
                    {reviewClone.sourceYear}
                  </p>
                </div>
                <div className="mt-2">
                  {renderNewOrganizationScope(row, true)}
                </div>
              </div>
              <div className="flex min-h-32 flex-col rounded-md border border-border bg-muted/20 p-3">
                <div className="flex justify-between items-center">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">
                    {t("Existing target orgs")}
                  </p>
                  <p className="mt-1 text-sm font-medium text-foreground">
                    {reviewClone.targetYear}
                  </p>
                </div>
                <div className="mt-2">{renderAssignmentScope(row.target)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const buildCloneReview = useCallback(
    ({ sourceYear, targetYear }) => {
      const sourceItems = items.filter(
        (item) => Number(item.reportingYear) === sourceYear,
      );
      const targetItems = items.filter(
        (item) => Number(item.reportingYear) === targetYear,
      );
      const targetByDisclosureId = new Map(
        targetItems.map((item) => [getDisclosureKey(item), item]),
      );
      const targetReportingYear = (reportingYears || []).find(
        (year) => Number(year?.startYear) === targetYear,
      );

      const sourceEligibilityRows = sourceItems.map((sourceItem) => {
        const disclosureId = getDisclosureKey(sourceItem);
        const eligibility = getCloneEligibility(
          sourceItem,
          targetReportingYear,
          companyId,
        );

        return {
          disclosureId,
          sourceItem,
          ...eligibility,
        };
      });
      const mismatchRows = sourceEligibilityRows
        .filter((row) => row.disclosureId && row.isMismatch)
        .map((row) => ({
          disclosureId: row.disclosureId,
          code: row.sourceItem.disclosureCode,
          title: row.sourceItem.disclosureTitle,
          framework: row.sourceItem.framework,
          category: row.sourceItem.disclosureCategory,
          reason: row.reason,
        }));
      const mismatchIdSet = new Set(
        mismatchRows.map((row) => row.disclosureId),
      );
      const cloneableSourceItems = sourceEligibilityRows
        .filter(
          (row) => row.disclosureId && !mismatchIdSet.has(row.disclosureId),
        )
        .map((row) => row.sourceItem);

      const duplicateRows = cloneableSourceItems
        .map((sourceItem) => {
          const disclosureId = getDisclosureKey(sourceItem);
          const targetItem = targetByDisclosureId.get(disclosureId);
          if (!disclosureId || !targetItem) return null;
          const scopeComparison = getCloneScopeComparison(
            sourceItem,
            targetItem,
            companyId,
          );

          return {
            disclosureId,
            code: sourceItem.disclosureCode,
            title: sourceItem.disclosureTitle,
            framework: sourceItem.framework,
            category: sourceItem.disclosureCategory,
            source: sourceItem,
            target: targetItem,
            ...scopeComparison,
          };
        })
        .filter(Boolean);

      const duplicateIdSet = new Set(
        duplicateRows.map((row) => row.disclosureId),
      );
      const nonDuplicateDisclosureIds = cloneableSourceItems
        .map(getDisclosureKey)
        .filter((id) => id && !duplicateIdSet.has(id));

      return {
        sourceYear,
        targetYear,
        duplicateRows,
        nonDuplicateDisclosureIds,
        mismatchRows,
        excludeDisIds: Array.from(mismatchIdSet),
        sourceCount: sourceItems.length,
        targetExistingCount: targetItems.length,
        hasTargetData: targetItems.length > 0,
        mergeCount: duplicateRows.filter((row) => !row.isSkip).length,
        createCount: nonDuplicateDisclosureIds.length,
        skipCount: duplicateRows.filter((row) => row.isSkip).length,
      };
    },
    [companyId, items, reportingYears],
  );

  const selectedSourceYear = Number.parseInt(form.sourceYear, 10);
  const selectedTargetYear = Number.parseInt(form.targetYear, 10);
  const hasSelectedYears =
    Number.isFinite(selectedSourceYear) &&
    Number.isFinite(selectedTargetYear) &&
    selectedSourceYear !== selectedTargetYear;
  const selectedTargetExistingCount = useMemo(() => {
    if (!Number.isFinite(selectedTargetYear)) return 0;

    return items.filter(
      (item) => Number(item.reportingYear) === selectedTargetYear,
    ).length;
  }, [items, selectedTargetYear]);
  const targetYearHasExistingData = selectedTargetExistingCount > 0;
  const liveCloneReview = useMemo(() => {
    if (!hasSelectedYears) return null;

    return buildCloneReview({
      sourceYear: selectedSourceYear,
      targetYear: selectedTargetYear,
    });
  }, [
    buildCloneReview,
    hasSelectedYears,
    selectedSourceYear,
    selectedTargetYear,
  ]);

  const executeClone = async ({
    sourceYear,
    targetYear,
    selectedDisclosureIds,
    excludeDisIds = [],
  }) => {
    if (!companyId) {
      showToast.error(t("Company is required."));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        companyId,
        sourceYear,
        targetYear,
        //excludeDisIds, for clone year if needed
      };
      if (Array.isArray(selectedDisclosureIds)) {
        payload.selectedDisclosureIds = selectedDisclosureIds;
      }

      const clonePromise = masterDataService.cloneByYear(payload);

      showToast.promise(clonePromise, {
        loading: t("Cloning metric assignments..."),
        success: (data) => {
          const createdCount = Number(data?.createdCount) || 0;
          const skippedCount = Number(data?.skippedCount) || 0;
          const updatedCount = Number(data?.updatedCount) || 0;

          if (createdCount > 0 || updatedCount > 0) {
            let message = [];

            if (createdCount > 0) {
              message.push(
                t("{{count}} metric assignment(s) cloned.", {
                  count: createdCount,
                }),
              );
            }

            if (updatedCount > 0) {
              message.push(
                t("{{count}} metric assignment(s) updated.", {
                  count: updatedCount,
                }),
              );
            }

            if (skippedCount > 0) {
              message.push(
                t("{{count}} skipped.", {
                  count: skippedCount,
                }),
              );
            }

            return message.join(" ");
          }

          return t("No metric assignments were processed.");
        },
        error: (error) =>
          error?.message || t("Failed to clone metric assignments."),
      });

      await clonePromise;
      onOpenChange(false);
      await onCloned?.();
    } catch {
      // Error toast handled by showToast.promise
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    const sourceYear = selectedSourceYear;
    const targetYear = selectedTargetYear;

    if (!Number.isFinite(sourceYear) || !Number.isFinite(targetYear)) {
      showToast.error(t("Please select both source year and target year."));
      return;
    }

    if (sourceYear === targetYear) {
      showToast.error(t("Source year and target year must be different."));
      return;
    }

    const review = buildCloneReview({ sourceYear, targetYear });

    if (
      review.duplicateRows.length === 0 &&
      review.nonDuplicateDisclosureIds.length === 0 &&
      review.mismatchRows.length === 0
    ) {
      showToast.error(t("No existing source year found for this company."));
      return;
    }

    setReviewImpactClone(review);
    setImpactAccepted(false);
  };
  const handleConfirmProceed = async () => {
    if (!confirmClone) return;

    if (confirmClone.mergeCount > 0 && !impactAccepted) {
      showToast.error(t("Please confirm you understand the impact."));
      return;
    }

    if (confirmClone.mergeCount === 0) {
      await executeClone({
        sourceYear: confirmClone.sourceYear,
        targetYear: confirmClone.targetYear,
        selectedDisclosureIds: confirmClone.nonDuplicateDisclosureIds,
        excludeDisIds: confirmClone.excludeDisIds,
      });
      return;
    }

    const actionableDuplicateIds = confirmClone.duplicateRows
      .filter((row) => !row.isSkip)
      .map((row) => row.disclosureId);

    setReviewClone(confirmClone);
    setConfirmClone(null);
    setSelectedDuplicateIds(actionableDuplicateIds);
  };

  const handleImpactContinue = async () => {
    if (!reviewImpactClone) return;

    setConfirmClone(reviewImpactClone);
    setReviewImpactClone(null);
    setImpactAccepted(false);
  };

  const handleReviewSubmit = async () => {
    if (!reviewClone) return;

    const actionableDuplicateIdSet = new Set(
      (reviewClone.duplicateRows || [])
        .filter((row) => !row.isSkip)
        .map((row) => row.disclosureId),
    );
    const selectedActionableDuplicateIds = selectedDuplicateIds.filter((id) =>
      actionableDuplicateIdSet.has(id),
    );

    if (selectedActionableDuplicateIds.length === 0) {
      showToast.error(t("Select at least one disclosure to clone."));
      return;
    }

    const selectedDisclosureIds = Array.from(
      new Set([
        ...reviewClone.nonDuplicateDisclosureIds,
        ...selectedActionableDuplicateIds,
      ]),
    );

    await executeClone({
      sourceYear: reviewClone.sourceYear,
      targetYear: reviewClone.targetYear,
      selectedDisclosureIds,
      excludeDisIds: reviewClone.excludeDisIds,
    });
  };

  const actionableDuplicateRows = useMemo(
    () => (reviewClone?.duplicateRows || []).filter((row) => !row.isSkip),
    [reviewClone?.duplicateRows],
  );
  const actionableDuplicateIdSet = useMemo(
    () => new Set(actionableDuplicateRows.map((row) => row.disclosureId)),
    [actionableDuplicateRows],
  );
  const reviewSelectedTotal = selectedDuplicateIds.filter((id) =>
    actionableDuplicateIdSet.has(id),
  ).length;
  const cloneFrameworkOptions = useMemo(() => {
    const selectedCategory = String(cloneFilters.category || "All");
    const optionRows =
      selectedCategory === "All"
        ? actionableDuplicateRows
        : actionableDuplicateRows.filter(
            (row) => String(row.category || "") === selectedCategory,
          );
    const frameworks = Array.from(
      new Set(
        optionRows
          .map((row) => String(row.framework || "").trim())
          .filter(Boolean),
      ),
    ).sort();

    return [
      { value: "All", label: t("All") },
      ...frameworks.map((framework) => ({
        value: framework,
        label: framework,
      })),
    ];
  }, [cloneFilters.category, actionableDuplicateRows, t]);

  const cloneCategoryOptions = useMemo(() => {
    const selectedFramework = String(cloneFilters.framework || "All");
    const optionRows =
      selectedFramework === "All"
        ? actionableDuplicateRows
        : actionableDuplicateRows.filter(
            (row) => String(row.framework || "") === selectedFramework,
          );
    const categories = Array.from(
      new Set(
        optionRows
          .map((row) => String(row.category || "").trim())
          .filter(Boolean),
      ),
    ).sort();

    return [
      { value: "All", label: t("All") },
      ...categories.map((category) => ({
        value: category,
        label: category,
      })),
    ];
  }, [cloneFilters.framework, actionableDuplicateRows, t]);

  useEffect(() => {
    setCloneFilters((prev) => {
      const frameworkValues = new Set(
        cloneFrameworkOptions.map((option) => option.value),
      );
      const categoryValues = new Set(
        cloneCategoryOptions.map((option) => option.value),
      );
      const nextFramework = frameworkValues.has(prev.framework)
        ? prev.framework
        : "All";
      const nextCategory = categoryValues.has(prev.category)
        ? prev.category
        : "All";

      if (nextFramework === prev.framework && nextCategory === prev.category) {
        return prev;
      }

      return {
        ...prev,
        framework: nextFramework,
        category: nextCategory,
      };
    });
  }, [cloneCategoryOptions, cloneFrameworkOptions]);

  const filteredDuplicateRows = useMemo(() => {
    const normalizedSearch = cloneFilters.search.trim().toLowerCase();
    const selectedFramework = String(cloneFilters.framework || "All");
    const selectedCategory = String(cloneFilters.category || "All");

    return actionableDuplicateRows.filter((row) => {
      if (
        selectedFramework !== "All" &&
        String(row.framework || "") !== selectedFramework
      ) {
        return false;
      }

      if (
        selectedCategory !== "All" &&
        String(row.category || "") !== selectedCategory
      ) {
        return false;
      }

      if (!normalizedSearch) return true;

      return [row.code, row.title, row.framework, row.category]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [
    cloneFilters.category,
    cloneFilters.framework,
    cloneFilters.search,
    actionableDuplicateRows,
  ]);

  const filteredDuplicateIds = useMemo(
    () => filteredDuplicateRows.map((row) => row.disclosureId),
    [filteredDuplicateRows],
  );
  const allFilteredDuplicatesSelected =
    filteredDuplicateIds.length > 0 &&
    filteredDuplicateIds.every((id) => selectedDuplicateIds.includes(id));

  const handleSelectAllFilteredDuplicates = () => {
    setSelectedDuplicateIds((prev) =>
      Array.from(new Set([...prev, ...filteredDuplicateIds])),
    );
  };

  const handleUnselectAllFilteredDuplicates = () => {
    const filteredIdSet = new Set(filteredDuplicateIds);
    setSelectedDuplicateIds((prev) =>
      prev.filter((id) => !filteredIdSet.has(id)),
    );
  };

  const impactCounts = liveCloneReview || {
    mergeCount: 0,
    createCount: 0,
    skipCount: 0,
    excludeDisIds: [],
  };
  const reviewDuplicateTotal = actionableDuplicateRows.length;
  const reviewImpactRows = useMemo(() => {
    if (!reviewImpactClone) return [];

    const mergeRows = (reviewImpactClone.duplicateRows || []).map((row) => ({
      key: `duplicate-${row.disclosureId}`,
      metric: `${row.code} - ${row.title}`,
      framework: row.framework,
      category: row.category,
      action: row.isSkip ? "Skip" : "Merge",
      actionClassName: row.isSkip
        ? "text-slate-400"
        : "text-amber-600 font-semibold",
      reason: row.isSkip
        ? row.reason || "No changes"
        : row.changesToAllOrganizations
          ? "Source = All Orgs"
          : t("{{count}} new orgs in source", {
              count: row.newOrganizationIds?.length || 0,
            }),
    }));

    const duplicateIdSet = new Set(
      (reviewImpactClone.duplicateRows || []).map((row) => row.disclosureId),
    );
    const createRows = items
      .filter(
        (item) => Number(item.reportingYear) === reviewImpactClone.sourceYear,
      )
      .filter((item) => {
        const disclosureId = getDisclosureKey(item);
        return (
          disclosureId &&
          !duplicateIdSet.has(disclosureId) &&
          !(reviewImpactClone.excludeDisIds || []).includes(disclosureId)
        );
      })
      .map((item) => ({
        key: `create-${getDisclosureKey(item)}`,
        metric: `${item.disclosureCode} - ${item.disclosureTitle}`,
        framework: item.framework,
        category: item.disclosureCategory,
        action: "Create",
        actionClassName: "text-emerald-600 font-semibold",
        reason: t("Not in target year"),
      }));

    const mismatchRows = (reviewImpactClone.mismatchRows || []).map((row) => ({
      key: `mismatch-${row.disclosureId}`,
      metric: `${row.code} - ${row.title}`,
      framework: row.framework,
      category: row.category,
      action: "Exclude",
      actionClassName: "text-red-600 font-semibold",
      reason: t(row.reason),
    }));

    return [...mergeRows, ...createRows, ...mismatchRows];
  }, [items, reviewImpactClone, t]);
  const dialogTitle = reviewImpactClone
    ? t("Clone Metric Data - Step 2 : Review Impact")
    : reviewClone
      ? t("Clone Metric Data - Step 3 : Review Duplicates")
      : confirmClone
        ? t("Confirm Clone Metric Data ")
        : t("Clone Metric Data");
  const dialogDescription = reviewImpactClone
    ? t(
        "No data has been written yet. Review what will happen before proceeding.",
      )
    : reviewClone
      ? t(
          "Review and select which duplicate metrics to clone into {{targetYear}}.",
          {
            targetYear: reviewClone.targetYear,
          },
        )
      : confirmClone
        ? confirmClone.hasTargetData
          ? t("Cloning {{sourceYear}} to {{targetYear}}", {
              sourceYear: confirmClone.sourceYear,
              targetYear: confirmClone.targetYear,
            })
          : t("Cloning {{sourceYear}} to {{targetYear}}", {
              sourceYear: confirmClone.sourceYear,
              targetYear: confirmClone.targetYear,
            })
        : t("Clone disclosures from a source year to a target year.");

  const isSummaryAllSkip =
    reviewImpactClone &&
    reviewImpactClone.createCount === 0 &&
    reviewImpactClone.mergeCount === 0;

  const confirmHasMerge = (confirmClone?.mergeCount || 0) > 0;
  const confirmMismatchCount = confirmClone?.excludeDisIds?.length || 0;
  const isDirty =
    !!form.sourceYear ||
    !!form.targetYear ||
    !!reviewImpactClone ||
    !!reviewClone ||
    !!confirmClone;

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      isDirty={isDirty}
      contentClassName="max-h-none overflow-hidden"
      title={dialogTitle}
      description={dialogDescription}
      size={reviewClone || reviewImpactClone ? "2xl" : "lg"}
      footer={
        <div
          className={`flex w-full items-center gap-2 ${
            reviewImpactClone ? "justify-between" : "justify-end"
          }`}
        >
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (reviewClone) {
                setConfirmClone(reviewClone);
                setReviewClone(null);
                setSelectedDuplicateIds([]);
                return;
              }

              if (confirmClone) {
                setReviewImpactClone(confirmClone);
                setConfirmClone(null);
                setImpactAccepted(false);
                return;
              }

              if (reviewImpactClone) {
                setReviewImpactClone(null);
                setImpactAccepted(false);
                return;
              }

              onOpenChange(false);
            }}
          >
            {reviewClone || reviewImpactClone || confirmClone?.hasTargetData ? (
              <span className="inline-flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                {t("Back")}
              </span>
            ) : (
              t("Cancel")
            )}
          </Button>
          {/* {reviewImpactClone ? (
            <p className="hidden text-xs font-semibold italic text-teal-600 sm:block">
              {t("Full read-only preview - no writes yet")}
            </p>
          ) : null} */}
          {isSummaryAllSkip ? null : (
            <Button
              type="button"
              onClick={() => {
                void (reviewClone
                  ? handleReviewSubmit()
                  : confirmClone
                    ? handleConfirmProceed()
                    : reviewImpactClone
                      ? handleImpactContinue()
                      : handleSubmit());
              }}
              disabled={
                submitting ||
                loading ||
                sourceYearOptions.length === 0 ||
                (reviewClone && reviewSelectedTotal === 0) ||
                (confirmClone?.hasTargetData &&
                  confirmClone?.mergeCount > 0 &&
                  !impactAccepted) ||
                isSummaryAllSkip
              }
              className={
                reviewClone || reviewImpactClone
                  ? "bg-emerald-600 text-white hover:bg-emerald-700"
                  : confirmClone?.mergeCount > 0
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : undefined
              }
            >
              {submitting ? (
                t("Cloning...")
              ) : reviewClone ? (
                <span className="inline-flex items-center gap-2">
                  {t("Clone Selected")}
                  <Check className="h-4 w-4" />
                </span>
              ) : reviewImpactClone ? (
                <span className="inline-flex items-center gap-2">
                  {t("Continue to Confirm")}
                  <ArrowRight className="h-4 w-4" />
                </span>
              ) : confirmClone ? (
                confirmClone.mergeCount > 0 ? (
                  <span className="inline-flex items-center gap-2">
                    {t("Proceed to Review")}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                ) : (
                  t("Clone")
                )
              ) : (
                <span className="inline-flex items-center gap-2">
                  {t("Next")}
                  <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-4 py-1">
        {reviewImpactClone ? (
          <div className="space-y-4">
            <div className="inline-flex min-w-72 items-center justify-center border border-border bg-muted/40 px-6 py-3 text-sm font-semibold text-foreground">
              {t("Reporting Year:")}{" "}
              <span className="ml-2">
                {reviewImpactClone.sourceYear}{" "}
                <ArrowRight className="mx-1 inline h-4 w-4" />{" "}
                {reviewImpactClone.targetYear}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-5">
              <div className="border border-border bg-slate-100 p-5 text-center shadow-sm">
                <p className="text-5xl font-bold text-slate-800">
                  {reviewImpactClone.sourceCount}
                </p>
                <p className="mt-3 text-sm font-semibold text-slate-900">
                  {t("Total Metrics")}
                </p>
                <p className="mt-2 text-xs text-slate-700">
                  {t("in source year")}
                </p>
              </div>
              <div className="border border-emerald-100 bg-emerald-100 p-5 text-center shadow-sm">
                <p className="text-5xl font-bold text-emerald-600">
                  {reviewImpactClone.createCount}
                </p>
                <p className="mt-3 text-sm font-semibold text-emerald-700">
                  {t("Will Create")}
                </p>
                <p className="mt-2 text-xs text-slate-700">
                  {t("new in target")}
                </p>
              </div>
              <div className="border border-amber-100 bg-amber-100 p-5 text-center shadow-sm">
                <p className="text-5xl font-bold text-amber-600">
                  {reviewImpactClone.mergeCount}
                </p>
                <p className="mt-3 text-sm font-semibold text-amber-600">
                  {t("Will Merge")}
                </p>
                <p className="mt-2 text-xs text-slate-700">{t("orgs added")}</p>
              </div>
              <div className="border border-slate-200 bg-slate-100 p-5 text-center shadow-sm">
                <p className="text-5xl font-bold text-slate-600">
                  {reviewImpactClone.skipCount}
                </p>
                <p className="mt-3 text-sm font-semibold text-slate-900">
                  {t("Will Skip")}
                </p>
                <p className="mt-2 text-xs text-slate-700">{t("no changes")}</p>
              </div>
              <div className="border border-red-100 bg-red-100 p-5 text-center shadow-sm">
                <p className="text-5xl font-bold text-red-600">
                  {(reviewImpactClone.excludeDisIds || []).length}
                </p>
                <p className="mt-3 text-sm font-semibold text-red-700">
                  {t("Will Exclude")}
                </p>
                <p className="mt-2 text-xs text-slate-700">{t("mismatch")}</p>
              </div>
            </div>

            <div className="overflow-hidden border border-border">
              <div className="max-h-[30vh] overflow-y-auto">
                <table className="w-full border-collapse text-left text-xs">
                  <thead className="sticky top-0 z-10 bg-muted ">
                    <tr>
                      <th className="border-b border-border px-3 py-2 font-semibold">
                        {t("Metric")}
                      </th>
                      <th className="border-b border-border px-3 py-2 font-semibold">
                        {t("Framework")}
                      </th>
                      <th className="border-b border-border px-3 py-2 font-semibold">
                        {t("Category")}
                      </th>
                      <th className="border-b border-border px-3 py-2 font-semibold">
                        {t("Action")}
                      </th>
                      <th className="border-b border-border px-3 py-2 font-semibold">
                        {t("Reason")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewImpactRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-3 py-8 text-center text-muted-foreground"
                        >
                          {t("No metric changes to preview.")}
                        </td>
                      </tr>
                    ) : (
                      reviewImpactRows.map((row) => (
                        <tr key={row.key} className=" ">
                          <td className="border-b border-border px-3 py-2">
                            {row.metric}
                          </td>

                          <td className="border-b border-border px-3 py-2">
                            {row.framework}
                          </td>

                          <td className="border-b border-border px-3 py-2">
                            {row.category}
                          </td>

                          <td
                            className={`border-b border-border px-3 py-2 ${row.actionClassName}`}
                          >
                            {t(row.action)}
                          </td>

                          <td className="border-b border-border px-3 py-2">
                            {row.reason}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : reviewClone ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
              <div className="flex items-center justify-center border border-2  px-4 py-3 text-sm font-semibold text-foreground lg:col-span-5 bg-muted">
                {t("Reporting Year:")}{" "}
                <span className="ml-2">
                  {reviewClone.sourceYear}{" "}
                  <ArrowRight className="mx-1 inline h-4 w-4" />{" "}
                  {reviewClone.targetYear}
                </span>
              </div>
              <div className="border border-2 px-4 py-2 text-center text-xs text-foreground lg:col-span-2 bg-muted">
                <p className="font-bold">
                  {reviewSelectedTotal} / {reviewDuplicateTotal}
                </p>
                <p>{t("selected")}</p>
              </div>
              {/* <div className="border border-border px-4 py-2 text-center text-xs text-emerald-700 lg:col-span-2">
                <p className="font-bold">
                  {reviewNewOrgTotal} {t("new org(s)")}
                </p>
                <p className="text-[11px] text-foreground">
                  {filteredDuplicateRows[0]?.code || "-"}
                </p>
              </div> */}
              {/* <div className="border border-border  px-4 py-2 text-center text-xs text-amber-600 lg:col-span-3">
                <p className="font-bold">{t("All Orgs")}</p>
                <p className="text-[11px] text-foreground">
                  {reviewAllOrgsTotal} {t("impact")}
                </p>
              </div> */}
            </div>

            <div className="grid grid-cols-1 items-center gap-3 p-2 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <label className="mb-1 block text-xs font-semibold text-muted-foreground">
                  {t("Search")}
                </label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={cloneFilters.search}
                    onChange={(event) =>
                      setCloneFilters((prev) => ({
                        ...prev,
                        search: event.target.value,
                      }))
                    }
                    placeholder={t("Search duplicate disclosures...")}
                    className="h-9 rounded bg-background pl-9 text-xs"
                  />
                </div>
              </div>

              <div className="lg:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-muted-foreground">
                  {t("Framework")}
                </label>
                <SearchableSelect
                  value={cloneFilters.framework}
                  onChange={(value) =>
                    setCloneFilters((prev) => ({
                      ...prev,
                      framework: value || "All",
                    }))
                  }
                  options={cloneFrameworkOptions}
                  placeholder={t("Framework")}
                />
              </div>

              <div className="lg:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-muted-foreground">
                  {t("Category")}
                </label>
                <SearchableSelect
                  value={cloneFilters.category}
                  onChange={(value) =>
                    setCloneFilters((prev) => ({
                      ...prev,
                      category: value || "All",
                    }))
                  }
                  options={cloneCategoryOptions}
                  placeholder={t("Category")}
                />
              </div>

              <div className="lg:col-span-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 rounded mt-5 text-xs font-semibold"
                  onClick={
                    allFilteredDuplicatesSelected
                      ? handleUnselectAllFilteredDuplicates
                      : handleSelectAllFilteredDuplicates
                  }
                  disabled={filteredDuplicateIds.length === 0}
                >
                  {allFilteredDuplicatesSelected
                    ? t("Unselect all")
                    : t("Select all")}
                </Button>
              </div>
            </div>

            <div className="max-h-[48vh] space-y-3 overflow-y-auto pr-1">
              {filteredDuplicateRows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                  {t(
                    "No cloneable duplicate disclosures match the current filters.",
                  )}
                </div>
              ) : null}

              {filteredDuplicateRows.map((row) =>
                renderDuplicateRow(row, { selectable: true }),
              )}
            </div>
          </div>
        ) : confirmClone ? (
          <div className="space-y-6">
            <div
              className={`flex items-start gap-3 border px-4 py-3 text-sm ${
                confirmHasMerge
                  ? "border-red-500 bg-red-50 text-red-900"
                  : "border-emerald-500 bg-emerald-50 text-emerald-900"
              }`}
            >
              {confirmHasMerge ? (
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              ) : (
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
              )}
              <p>
                {confirmClone.hasTargetData
                  ? confirmClone.mergeCount === 0
                    ? t(
                        "{{targetYear}} already has metric assignments.This will create {{count}} new metric(s).",
                        {
                          targetYear: confirmClone.targetYear,
                          count: confirmClone.createCount,
                        },
                      )
                    : t(
                        "{{targetYear}} already has metric assignments. This will modify existing data.",
                        {
                          targetYear: confirmClone.targetYear,
                        },
                      )
                  : t(
                      "{{targetYear}} has no existing data. All {{count}} metrics will be created fresh.",
                      {
                        targetYear: confirmClone.targetYear,
                        count: confirmClone.createCount,
                      },
                    )}
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-foreground">
                {confirmClone.hasTargetData
                  ? t("What will happen:")
                  : t("Impact")}
              </p>
              {confirmMismatchCount > 0 ? (
                <div className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {t(
                    "{{count}} metric(s) will be excluded because the target reporting year does not include their framework or organizations.",
                    { count: confirmMismatchCount },
                  )}
                </div>
              ) : null}
              {confirmClone.hasTargetData ? (
                <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
                  <li>
                    {t("{{count}} metrics will be created in {{targetYear}}", {
                      count: confirmClone.createCount,
                      targetYear: confirmClone.targetYear,
                    })}
                  </li>
                  <li>
                    {t(
                      "{{count}} existing metrics will have new orgs merged in",
                      {
                        count: confirmClone.mergeCount,
                      },
                    )}
                  </li>
                  <li>
                    {t("{{count}} metrics will be skipped (no changes)", {
                      count: confirmClone.skipCount,
                    })}
                  </li>
                </ul>
              ) : (
                <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
                  <li>
                    {t("{{count}} metrics will be created", {
                      count: confirmClone.createCount,
                    })}
                  </li>
                  <li>{t("0 existing records affected")}</li>
                  <li>{t("Action is fully reversible")}</li>
                </ul>
              )}
            </div>

            {confirmClone.mergeCount > 0 ? (
              <label className="flex cursor-pointer items-start gap-3 border-2 border-red-500 bg-red-50 p-4 text-sm font-semibold text-red-700">
                <Checkbox
                  checked={impactAccepted}
                  onCheckedChange={(value) => setImpactAccepted(Boolean(value))}
                  className="mt-0.5 border-red-600 data-[state=checked]:border-red-600 data-[state=checked]:bg-red-600"
                />
                <span>
                  {t(
                    "I understand this will modify existing {{targetYear}} metric data and cannot be undone.",
                    {
                      targetYear: confirmClone.targetYear,
                    },
                  )}
                </span>
              </label>
            ) : null}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <p className="text-sm font-medium text-foreground">
                  {t("Source year")}
                </p>
                <SearchableSelect
                  value={form.sourceYear}
                  onChange={(value) =>
                    setForm((prev) => ({ ...prev, sourceYear: value || "" }))
                  }
                  options={sourceYearOptions}
                  placeholder={t("Select source year")}
                  disabled={loading || sourceYearOptions.length === 0}
                />
              </div>
              <div className="space-y-1.5">
                <p className="text-sm font-medium text-foreground">
                  {t("Target year")}
                </p>
                <SearchableSelect
                  value={form.targetYear}
                  onChange={(value) =>
                    setForm((prev) => ({ ...prev, targetYear: value || "" }))
                  }
                  options={targetYearOptions.filter(
                    (option) => option.value !== form.sourceYear,
                  )}
                  placeholder={t("Select target year")}
                  disabled={loading || !form.sourceYear}
                />
              </div>
            </div>

            {hasSelectedYears &&
            liveCloneReview?.createCount === 0 &&
            liveCloneReview?.mergeCount === 0 ? (
              <div className="flex items-start gap-3 border border-amber-500 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <p>
                  {(liveCloneReview?.excludeDisIds || []).length > 0
                    ? t(
                        "Nothing need to clone. Some disclosures will be excluded because the target reporting year does not include their framework or organizations.",
                      )
                    : t(
                        "Nothing need to clone. All disclosures already exist in the target year.",
                      )}
                </p>
              </div>
            ) : targetYearHasExistingData ? (
              <div className="flex items-start gap-3 border border-amber-500 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <p>
                  {t(
                    "{{targetYear}} already has metric assignments. Proceeding may modify existing data.",
                    { targetYear: selectedTargetYear },
                  )}
                </p>
              </div>
            ) : null}

            {hasSelectedYears ? (
              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-4 text-center text-sm font-semibold sm:grid-cols-4">
                  <div className="bg-amber-100 px-3 py-2 text-amber-700">
                    {impactCounts.mergeCount} {t("merge")}
                  </div>
                  <div className="bg-emerald-100 px-3 py-2 text-emerald-700">
                    {impactCounts.createCount} {t("create")}
                  </div>
                  <div className="bg-slate-100 px-3 py-2 text-slate-700">
                    {impactCounts.skipCount} {t("skip")}
                  </div>
                  <div className="bg-red-100 px-3 py-2 text-red-700">
                    {(impactCounts.excludeDisIds || []).length} {t("exclude")}
                  </div>
                </div>
                <p className="text-center text-xs text-muted-foreground">
                  {t("Quick impact estimate for {{targetYear}}:", {
                    targetYear: selectedTargetYear,
                  })}
                </p>
              </div>
            ) : null}

            <div className="space-y-2">
              {sourceYearOptions.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {t("No existing source year found for this company.")}
                </p>
              ) : null}
              {!form.targetYear ? (
                <p className="text-xs text-muted-foreground">
                  {t(
                    "If the target year already has the same disclosure, you will review duplicates before cloning.",
                  )}
                </p>
              ) : null}
            </div>
          </>
        )}
      </div>
    </AppDialog>
  );
};

export default MetricsCloneDialog;
